import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Modal } from '@safetyhub/ui';
import {
  formatDateToLocal,
  type LegalObligationEvidenceView,
  type LegalObligationIntervalVariantView,
  type LegalObligationPendingResultView,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import {
  downloadLegalObligationEvidenceFile,
  getLegalObligationResultEvidences,
  legalObligationEvidenceQueryKeys,
  relinkLegalObligationEvidenceResult,
} from '../../lib/legalObligationEvidence';
import { describeMeasurementResult, EVIDENCE_KIND_LABEL } from '../../lib/legalDocumentLabels';
import { useToastStore } from '../../stores/toast';
import ResultRelinkModal from './ResultRelinkModal';

export interface ResultEvidenceListModalProps {
  open: boolean;
  companyId: string;
  siteId: string;
  /** 측정·진단 결과 행 id — 서버 `latestResult.entryId` */
  entryId: string;
  obligationTitle: string;
  /** `obligation.manage` — 있으면 「결과표 연결 바꾸기」를 보인다 */
  canManage: boolean;
  /** 같은 판정 단위의 결과표 없는 결과(연결 바꾸기의 선택지) */
  pendingResults: readonly LegalObligationPendingResultView[];
  resultVariants: ReadonlyArray<
    Pick<LegalObligationIntervalVariantView, 'selector' | 'valueKeys' | 'label'>
  >;
  /** 연결을 바꾼 뒤 — 의무 목록(결과표 등록 판정·미연결 결과)을 다시 읽게 한다 */
  onResultRelinked: () => void;
  onClose: () => void;
}

/**
 * 한 측정·진단 결과에 이어진 결과표(B+ 2차 · 사용자 확인 2026-11-02) — 결과표는 «그 측정의 것»이라 측정 결과 줄에서 연다.
 * 인스턴스 목록(`EvidenceListModal`)과 달리 **기간을 가로지른다**: 결과표는 그 측정이 든 기간에 붙고 그 기간이 이미 지났을 수 있다
 * (종전엔 「지금 기간」 목록에서만 연결 바꾸기가 닿아 지난 기간 결과표를 고칠 길이 없었다). 행마다 붙은 기간을 보인다.
 */
export default function ResultEvidenceListModal({
  open,
  companyId,
  siteId,
  entryId,
  obligationTitle,
  canManage,
  pendingResults,
  resultVariants,
  onResultRelinked,
  onClose,
}: ResultEvidenceListModalProps) {
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [relinkTarget, setRelinkTarget] = useState<LegalObligationEvidenceView | null>(null);
  const [relinkError, setRelinkError] = useState<string | null>(null);
  const queryKey = legalObligationEvidenceQueryKeys.resultEvidences(companyId, siteId, entryId);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getLegalObligationResultEvidences(companyId, siteId, entryId),
    enabled: open,
  });

  const relinkMutation = useMutation({
    mutationFn: ({
      evidenceId,
      resultEntryId,
      reason,
    }: {
      evidenceId: string;
      resultEntryId: string | null;
      reason: string;
    }) =>
      relinkLegalObligationEvidenceResult(companyId, siteId, evidenceId, { resultEntryId, reason }),
    onSuccess: async () => {
      setRelinkTarget(null);
      showToast('결과표 연결을 바꿨습니다.', 'success');
      await queryClient.invalidateQueries({ queryKey });
      onResultRelinked();
    },
    onError: (err) => setRelinkError(apiErrorMessage(err) ?? '결과표 연결을 바꾸지 못했습니다.'),
  });

  const download = async (evidence: LegalObligationEvidenceView) => {
    if (!evidence.file || downloading) return;
    setDownloading(evidence.evidenceId);
    try {
      const blob = await downloadLegalObligationEvidenceFile(
        companyId,
        siteId,
        evidence.evidenceId
      );
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = evidence.file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소한다 — 형제 `EvidenceListModal`과 같은 처리
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '문서 파일을 내려받지 못했습니다.', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const evidences = data?.evidences ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="결과표"
      size="md"
      footer={
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            닫기
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span>
          {data ? ` · ${describeMeasurementResult(data.result, resultVariants)} 측정` : null}
        </p>

        {isLoading ? (
          <p className="text-sm text-ink-500" role="status">
            결과표를 불러오는 중...
          </p>
        ) : error ? (
          <p
            role="alert"
            className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {apiErrorMessage(error) ?? '결과표를 불러오지 못했습니다.'}
          </p>
        ) : evidences.length === 0 ? (
          <p className="text-sm text-ink-500">이 측정에 이어진 결과표가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-ink-200" aria-label="결과표 목록">
            {evidences.map((e) => (
              <li key={e.evidenceId} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink-900">
                      {EVIDENCE_KIND_LABEL[e.kind]}
                      {e.late && (
                        <Badge tone="amber" size="sm" className="ml-2">
                          늦은 이행
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-ink-500">
                      {e.periodKey} 기간 · 이행일 {formatDateToLocal(new Date(e.fulfilledAt))}
                      {e.attachedByName ? ` · ${e.attachedByName}` : ''}
                    </p>
                    {e.report && (
                      <Link
                        to={`/reports/${encodeURIComponent(e.report.reportId)}`}
                        className="mt-1 block truncate text-sm text-brand-600 hover:underline"
                      >
                        {e.report.title}
                      </Link>
                    )}
                    {e.note && (
                      <p className="mt-1 whitespace-pre-wrap text-sm text-ink-700">{e.note}</p>
                    )}
                    {canManage && (
                      <button
                        type="button"
                        className="mt-1 text-xs font-medium text-brand-600 hover:underline"
                        onClick={() => {
                          setRelinkError(null);
                          setRelinkTarget(e);
                        }}
                      >
                        결과표 연결 바꾸기
                      </button>
                    )}
                  </div>
                  {e.file && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => download(e)}
                      disabled={downloading === e.evidenceId}
                    >
                      {downloading === e.evidenceId ? '받는 중...' : e.file.filename}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {relinkTarget && (
        <ResultRelinkModal
          key={relinkTarget.evidenceId}
          open
          current={relinkTarget.result}
          pendingResults={pendingResults}
          resultVariants={resultVariants}
          submitting={relinkMutation.isPending}
          errorMessage={relinkError}
          onSubmit={({ resultEntryId, reason }) => {
            setRelinkError(null);
            relinkMutation.mutate({ evidenceId: relinkTarget.evidenceId, resultEntryId, reason });
          }}
          onClose={() => setRelinkTarget(null)}
        />
      )}
    </Modal>
  );
}
